import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  FlatListProps,
  NativeScrollEvent,
  NativeSyntheticEvent,
  ScrollView,
  View,
} from 'react-native';

interface FlatListV2Props<ItemT> extends Omit<FlatListProps<ItemT>, 'ref'> {
  // Optional virtual scrolling configuration (for fine-tuning)
  virtualScrollThreshold?: number; // Minimum items to enable virtual scrolling on web
  bufferSize?: number; // Extra items to render above/below visible area
  itemHeight?: number; // Height of each item (inferred from getItemLayout if not provided)
  containerHeight?: number; // Height of the scrollable container (inferred from style if not provided)
}

interface FlatListV2Ref<ItemT> {
  scrollToIndex: (params: {
    index: number;
    animated?: boolean;
    viewPosition?: number;
  }) => void;
  scrollToOffset: (params: { offset: number; animated?: boolean }) => void;
  scrollToTop: (params?: { animated?: boolean }) => void;
  scrollToEnd: (params?: { animated?: boolean }) => void;
  getScrollRef: () => ScrollView | null;
}

function FlatListV2<ItemT>(
  props: FlatListV2Props<ItemT>,
  ref: React.Ref<FlatListV2Ref<ItemT>>
) {
  const {
    data,
    renderItem,
    keyExtractor,
    ListEmptyComponent,
    onScroll,
    getItemLayout,
    onScrollToIndexFailed,
    style,
    contentContainerStyle,
    // Virtual scrolling config (optional overrides)
    virtualScrollThreshold,
    bufferSize,
    itemHeight: providedItemHeight,
    containerHeight: providedContainerHeight,
    ...restProps
  } = props;

  // Ensure data is always an array
  const safeData = useMemo(() => data || [], [data]);

  // Refs
  const scrollViewRef = useRef<ScrollView>(null);

  // State for virtual scrolling
  const [scrollOffset, setScrollOffset] = useState(0);
  const [measuredContainerHeight, setMeasuredContainerHeight] = useState<
    number | null
  >(null);

  // Infer item height from getItemLayout or use default
  const inferredItemHeight = useMemo(() => {
    if (providedItemHeight) return providedItemHeight;

    if (getItemLayout && safeData.length > 0) {
      try {
        const layout = getItemLayout(safeData, 0);

        return layout.length;
      } catch {
        // Fallback if getItemLayout fails
      }
    }

    // Default item height (common for list items)
    return 56;
  }, [providedItemHeight, getItemLayout, safeData]);

  // Infer container height from measured dimensions, props, or style
  const inferredContainerHeight = useMemo(() => {
    // Use measured height from onLayout
    if (measuredContainerHeight) return measuredContainerHeight;

    // Use explicitly provided height
    if (providedContainerHeight) return providedContainerHeight;

    // Try to extract height from style prop
    if (style) {
      const styleArray = Array.isArray(style) ? style : [style];

      for (const s of styleArray) {
        if (s && typeof s === 'object' && 'height' in s) {
          const height = s.height;

          if (typeof height === 'number') {
            return height;
          }
        }
      }
    }

    // Default container height for modals/lists
    return 400;
  }, [measuredContainerHeight, providedContainerHeight, style]);

  // Virtual scrolling configuration with smart defaults
  const virtualScrollConfig = useMemo(
    () => ({
      threshold: virtualScrollThreshold ?? 50, // Enable for 50+ items by default
      bufferSize: bufferSize ?? 5, // 5 items buffer by default
      itemHeight: inferredItemHeight,
      containerHeight: inferredContainerHeight,
    }),
    [
      virtualScrollThreshold,
      bufferSize,
      inferredItemHeight,
      inferredContainerHeight,
    ]
  );

  // Virtual scrolling logic - only activate when beneficial
  const virtualScrolling = safeData.length > virtualScrollConfig.threshold;

  const visibleRange = useMemo(() => {
    if (!virtualScrolling) {
      return { startIndex: 0, endIndex: safeData.length - 1 };
    }

    const { itemHeight, containerHeight, bufferSize } = virtualScrollConfig;

    const startIndex = Math.max(
      0,
      Math.floor(scrollOffset / itemHeight) - bufferSize
    );
    const endIndex = Math.min(
      safeData.length - 1,
      startIndex + Math.ceil(containerHeight / itemHeight) + bufferSize * 2
    );

    return { startIndex, endIndex };
  }, [virtualScrolling, scrollOffset, safeData.length, virtualScrollConfig]);

  const visibleData = useMemo(() => {
    if (!virtualScrolling) {
      return safeData;
    }

    return Array.isArray(safeData)
      ? safeData.slice(visibleRange.startIndex, visibleRange.endIndex + 1)
      : [];
  }, [virtualScrolling, safeData, visibleRange]);

  // Ensure visibleData is always an array for mapping
  const renderableData = Array.isArray(visibleData)
    ? visibleData
    : Array.from(visibleData || []);

  const topSpacerHeight = virtualScrolling
    ? visibleRange.startIndex * virtualScrollConfig.itemHeight
    : 0;
  const bottomSpacerHeight = virtualScrolling
    ? (safeData.length - visibleRange.endIndex - 1) *
      virtualScrollConfig.itemHeight
    : 0;

  // Handle layout measurements for automatic container height detection
  const handleLayout = useCallback(
    (event: any) => {
      const { height } = event.nativeEvent.layout;

      if (height > 0 && height !== measuredContainerHeight) {
        setMeasuredContainerHeight(height);
      }
    },
    [measuredContainerHeight]
  );

  // Handle scroll events
  const handleScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      if (virtualScrolling) {
        setScrollOffset(event.nativeEvent.contentOffset.y);
      }

      onScroll?.(event);
    },
    [virtualScrolling, onScroll]
  );

  // Render item wrapper for virtual scrolling
  const renderVirtualItem = useCallback(
    (item: ItemT, index: number) => {
      if (!renderItem) return null;

      const actualIndex = virtualScrolling
        ? visibleRange.startIndex + index
        : index;

      return renderItem({ item, index: actualIndex, separators: {} as any });
    },
    [renderItem, virtualScrolling, visibleRange.startIndex]
  );

  // Imperative API
  useImperativeHandle(
    ref,
    () => ({
      scrollToIndex: (params) => {
        try {
          // Validate index bounds
          if (params.index < 0 || params.index >= safeData.length) {
            const error = new Error(
              `Index ${params.index} out of bounds for data length ${safeData.length}`
            );
            onScrollToIndexFailed?.({
              index: params.index,
              highestMeasuredFrameIndex: safeData.length - 1,
              averageItemLength: virtualScrollConfig.itemHeight,
            });

            return;
          }

          const offset = params.index * virtualScrollConfig.itemHeight;
          const centeredOffset = Math.max(
            0,
            offset - virtualScrollConfig.containerHeight / 2
          );

          scrollViewRef.current?.scrollTo({
            y: centeredOffset,
            animated: params.animated ?? false,
          });
        } catch (error) {
          onScrollToIndexFailed?.({
            index: params.index,
            highestMeasuredFrameIndex: safeData.length - 1,
            averageItemLength: virtualScrollConfig.itemHeight,
          });
        }
      },
      scrollToOffset: (params) => {
        scrollViewRef.current?.scrollTo({
          y: params.offset,
          animated: params.animated ?? false,
        });
      },
      scrollToTop: (params) => {
        scrollViewRef.current?.scrollTo({
          y: 0,
          animated: params?.animated ?? false,
        });
      },
      scrollToEnd: (params) => {
        scrollViewRef.current?.scrollToEnd({
          animated: params?.animated ?? false,
        });
      },
      getScrollRef: () => {
        return scrollViewRef.current;
      },
    }),
    [virtualScrollConfig, onScrollToIndexFailed, safeData.length]
  );

  // Web: Use ScrollView with virtual scrolling
  return (
    <ScrollView
      ref={scrollViewRef}
      style={style}
      contentContainerStyle={contentContainerStyle}
      onScroll={handleScroll}
      onLayout={handleLayout}
      scrollEventThrottle={16}
      {...restProps}
    >
      {/* Top spacer for virtual scrolling */}
      {virtualScrolling && topSpacerHeight > 0 ? (
        <View style={{ height: topSpacerHeight }} />
      ) : null}

      {/* Render visible items */}
      {renderableData.map((item, index) => {
        const key = keyExtractor ? keyExtractor(item, index) : String(index);

        return <View key={key}>{renderVirtualItem(item, index)}</View>;
      })}

      {/* Bottom spacer for virtual scrolling */}
      {virtualScrolling && bottomSpacerHeight > 0 ? (
        <View style={{ height: bottomSpacerHeight }} />
      ) : null}

      {/* Empty component */}
      {safeData.length === 0 && ListEmptyComponent ? (
        typeof ListEmptyComponent === 'function' ? (
          <ListEmptyComponent />
        ) : (
          ListEmptyComponent
        )
      ) : null}
    </ScrollView>
  );
}

export default forwardRef(FlatListV2) as <ItemT>(
  props: FlatListV2Props<ItemT> & { ref?: React.Ref<FlatListV2Ref<ItemT>> }
) => React.ReactElement;
